import apiClient from "./api";
import { getApiAccessToken } from "./UserService";

const baseUrl = '/api/CityObjectAttachments';

export interface AttachmentDto {
    id: number;
    originalName: string;
    size: number;
    fileHeader: string;
    created: string;
}

export interface UploadFileRequest {
    fileName: string;
    fileHeader: string;
    fileData: string; // Base64 encoded
    contentType: string;
    fileType: number;
}

export interface UploadAttachmentsRequest {
    perceptionInfoId: number;
    contractId: number;
    userId: number;
    addressSystemId: number;
    files: UploadFileRequest[];
}

export interface UploadResponse {
    message: string;
    uploadedCount: number;
}

export interface FileDownloadData {
    content: Blob;
    fileName: string;
}

/**
 * Get all attachments for a specific city object (perception) within a contract
 */
export const getAttachments = async (
    objectId: number,
    contractId: number,
    userId: number,
    addressSystemId: number
): Promise<AttachmentDto[]> => {
    const token = await getApiAccessToken();
    const response = await apiClient.get<AttachmentDto[]>(
        `${baseUrl}/${objectId}`,
        {
            params: { contractId, userId, addressSystemId },
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }
    );
    return response.data;
};

/**
 * Upload one or more attachments for a city object
 */
export const uploadAttachments = async (
    request: UploadAttachmentsRequest
): Promise<UploadResponse> => {
    const token = await getApiAccessToken();
    const response = await apiClient.post<UploadResponse>(
        `${baseUrl}/upload`,
        request,
        {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json',
            },
        }
    );
    return response.data;
};

/**
 * Download a specific attachment file
 */
export const downloadAttachment = async (
    fileId: number,
    contractId: number,
    objectId: number,
    userId: number,
    addressSystemId: number
): Promise<FileDownloadData> => {
    const token = await getApiAccessToken();
    const response = await apiClient.get<Blob>(
        `${baseUrl}/download/${fileId}`,
        {
            params: { contractId, objectId, userId, addressSystemId },
            headers: {
                Authorization: `Bearer ${token}`,
            },
            responseType: 'blob',
        }
    );

    // Extract filename from Content-Disposition header if available
    const contentDisposition = response.headers['content-disposition'];
    let fileName = 'download';
    if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (fileNameMatch && fileNameMatch[1]) {
            fileName = fileNameMatch[1];
        }
    }

    return {
        content: response.data as Blob,
        fileName,
    };
};

/**
 * Soft delete an attachment (sets filestatus to 0)
 */
export const deleteAttachment = async (
    fileId: number,
    contractId: number,
    objectId: number,
    userId: number,
    addressSystemId: number
): Promise<void> => {
    const token = await getApiAccessToken();
    await apiClient.delete(
        `${baseUrl}/${fileId}`,
        {
            params: { contractId, objectId, userId, addressSystemId },
            headers: {
                Authorization: `Bearer ${token}`,
            },
        }
    );
};
